import { cloneElement, isValidElement, ReactElement, ReactNode, Suspense, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { SidebarProvider, SidebarInset, SIDEBAR_WIDTH_ICON_PX } from '@/components/ui/sidebar';
import { AppSidebar } from './AppSidebar';
import { GeometricFacetBackground } from './GeometricFacetBackground';
import { useOrganization } from '@/contexts/OrganizationContext';
import { SessionManager } from '@/components/auth/SessionManager';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { DebugClipboardButton } from '@/components/debug/DebugClipboardButton';
import { SuperAdminDebugOverlay } from '@/components/debug/SuperAdminDebugOverlay';
import { SuperAdminHintsControl } from '@/components/debug/SuperAdminHintsButton';
import { AgentFAB } from '@/components/agent/AgentFAB';
import { BreakGlassCountdownBanner } from '@/components/breakglass/BreakGlassCountdownBanner';
import { OrgMFAEnforcementScreen } from '@/components/auth/OrgMFAEnforcementScreen';
import { MFAVerification } from '@/components/auth/MFAVerification';
import { Button } from '@/components/ui/button';
import {
  clearPortalSwitchIntent,
  readPortalSwitchIntent,
  subscribeToPortalSwitchIntent,
} from '@/lib/portalSwitch';
import { CUSTOMER_PROFILE_LABEL } from '@/lib/surfaceViewLabel';
import {
  SupportNoticeBanner,
  useActiveSupportNoticeBannerState,
} from './SupportNoticeBanner';
import { Loader2, Eye, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { isMfaRequired, isPreviewMfaBypass, shouldBlockProtectedRouteDuringRecovery } from '@/lib/auth';
import { isOrgMfaEnrollmentRequired } from '@/shared/mfaPolicy';
import { isSsoSession, type SsoSessionLike } from '@/shared/ssoSession';
import { buildAuthRedirectPath } from '@/lib/authReturnTo';
import { useSuperAdminStatus } from '@/hooks/useIsSuperAdmin';
import { useAalStepUpGate } from '@/hooks/useAalStepUpGate';
import { cn } from '@/lib/utils';
import { SubscriptionGate } from '@/components/auth/SubscriptionGate';
import { SurfaceSidebarContextToggleButton, FitToWidthText } from './SurfaceSidebar';
import { AppShellHeaderAccessoryContext } from './AppShellHeaderAccessoryContext';

interface AppLayoutProps {
  children?: ReactNode;
  requireOrg?: boolean;
  sessionTimeoutMinutes?: number;
}

// The provider already waits for the bootstrap timeout before this recovery loop runs.
// Keep these follow-up refetches short so the retry/sign-in recovery state appears promptly.
const ORG_RECOVERY_REFETCH_TIMEOUT_MS = 750;

// Exported for reuse as the in-shell lazy-chunk fallback of other portal shells
// (ENT-3079: the vendor layout route mirrors AppLayout's in-shell Suspense).
export function AppShellRouteLoadingState({ message = 'Loading your workspace…' }: { message?: string }) {
  return (
    <div
      className="flex min-h-[calc(100svh-8rem)] items-center justify-center"
      data-testid="app-shell-route-loading"
    >
      <div className="flex flex-col items-center gap-3 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}

function FullPageAppLoadingState({ message = 'Loading your workspace…' }: { message?: string }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6 dark:from-slate-950 dark:to-slate-900">
      <AppShellRouteLoadingState message={message} />
    </div>
  );
}

function OrganizationBootstrapErrorState({
  message,
  onRetry,
  onSignOut,
}: {
  message: string;
  onRetry: () => void;
  onSignOut: () => Promise<void>;
}) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6 dark:from-slate-950 dark:to-slate-900">
      <div className="mx-auto flex min-h-[calc(100svh-8rem)] max-w-lg items-center justify-center">
        <div className="w-full rounded-2xl border bg-background p-8 shadow-sm">
          <div className="space-y-3 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">We couldn&apos;t load your organisation</h1>
            <p className="text-sm text-muted-foreground">{message}</p>
          </div>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button onClick={onRetry}>Retry</Button>
            <Button variant="outline" onClick={() => void onSignOut()}>
              Return to sign in
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AppLayout({
  children, 
  requireOrg = true,
  sessionTimeoutMinutes = 15,
}: AppLayoutProps) {
  const {
    user,
    organization,
    isLoading,
    bootstrapError,
    isMspContext,
    mspHomeOrg,
    returnToMspOrg,
    refetch,
  } = useOrganization();
  const [mounted, setMounted] = useState(false);
  const [orgRetryAttempted, setOrgRetryAttempted] = useState(false);
  const [orgRetrying, setOrgRetrying] = useState(false);
  const [orgRetryCount, setOrgRetryCount] = useState(0);
  const [orgRetryRequestId, setOrgRetryRequestId] = useState(0);
  const [spinnerSince, setSpinnerSince] = useState<number | null>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [hasMFA, setHasMFA] = useState(false);
  const [mfaCheckComplete, setMfaCheckComplete] = useState(false);
  // D1 (ENT-3463): the current session, captured only to decide the org-MFA
  // enrolment exemption. An SSO session delegates MFA to the customer IdP, so
  // `isOrgMfaEnrollmentRequired` never forces OrgMFAEnforcementScreen for it.
  const [mfaSession, setMfaSession] = useState<SsoSessionLike | null>(null);
  // Super admins must have MFA enrolled regardless of their org's setting —
  // the AAL2 enforcement on sensitive admin actions is only meaningful if
  // every actor reachable through it has a factor on file. Force enrolment
  // via OrgMFAEnforcementScreen on next sign-in if a super admin has no
  // verified factor. Tracked in ENT-1140.
  //
  // Fail closed while the super_admin role lookup is in flight or errored:
  // we hold the workspace render until the role is known. Otherwise a slow
  // role lookup or a transient error would briefly default to `isSuperAdmin
  // === false`, letting an actual super admin bypass the force-enrolment
  // screen for the duration of the window.
  const superAdminStatus = useSuperAdminStatus(user?.id);
  const isSuperAdmin = superAdminStatus.isSuperAdmin;
  // ENT-4436 / ENT-5840: the floating Hints toggle renders only for a super admin while the
  // atlas_findings_view flag is on; its state is the global Hints preference.
  const superAdminLookupPending = superAdminStatus.isLoading || superAdminStatus.error;
  // When the lookup hasn't resolved (or errored), pessimistically treat the
  // user as if they might be a super admin so the MFA enforcement gate
  // fires. `mfaCheckComplete` will not flip true until both the org-MFA
  // factor lookup and the super-admin role lookup have settled.
  const treatAsPotentialSuperAdmin = isSuperAdmin || superAdminLookupPending;
  const [isReturningToMsp, setIsReturningToMsp] = useState(false);
  const [headerAccessory, setHeaderAccessory] = useState<ReactNode | null>(null);
  const [portalSwitchIntent, setPortalSwitchIntent] = useState(() => readPortalSwitchIntent());
  const lastCompletedMfaCheckKeyRef = useRef<string | null>(null);
  const activeExitRouteRef = useRef<string | null>(null);
  const isReturningToMspRef = useRef(false);
  const isMountedRef = useRef(true);
  const navigate = useNavigate();
  const location = useLocation();
  const currentRouteKey = `${location.pathname}${location.search}${location.hash}`;
  activeExitRouteRef.current = currentRouteKey;
  const isWorkspaceNoticeRoute = !location.pathname.startsWith('/vendor');

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    isReturningToMspRef.current = isReturningToMsp;
  }, [isReturningToMsp]);

  useEffect(() => subscribeToPortalSwitchIntent(setPortalSwitchIntent), []);

  // D1 (ENT-3463) — break-glass invariant: `mfaSession` (the input to the SSO
  // org-MFA exemption) must stay bound to the CURRENT session. This listener is
  // the SOLE writer of `mfaSession`; the org/policy probe below never writes it.
  // Two reasons:
  //   1. A same-user session swap (e.g. a cross-tab break-glass PASSWORD sign-in
  //      of an SSO account) fires onAuthStateChange but leaves this component's
  //      user/org/policy inputs unchanged, so the probe never re-runs. Without a
  //      dedicated listener `mfaSession` would stay the stale SSO token and
  //      silently exempt the replacement AAL1 password session.
  //   2. The probe reads `getSession()` before awaiting `listFactors()`. If a
  //      swap landed mid-probe, a probe that also wrote session state would
  //      clobber this listener's fresh value with the SSO session it captured
  //      pre-swap (a TOCTOU that re-opens the hole). Keeping the probe out of
  //      session state removes that race entirely.
  // `isSsoSession(mfaSession)` therefore fails closed (SSO→password ⇒
  // enrolment/step-up re-enforced) within one render. getSession /
  // onAuthStateChange read local storage only — no DB call on any critical path.
  useEffect(() => {
    let active = true;
    // sawAuthEvent: once any auth transition has set the session, the async
    // seed below must not overwrite it with a value that is already superseded.
    let sawAuthEvent = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!active || !isMountedRef.current || sawAuthEvent) return;
      setMfaSession(data?.session ?? null);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMountedRef.current) return;
      sawAuthEvent = true;
      setMfaSession(session ?? null);
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  // Ensure component is mounted before making redirect decisions
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    if (!requireOrg) return;
    if (!user) return;
    if (organization) {
      setSpinnerSince(null);
      return;
    }
    setSpinnerSince((prev) => prev ?? Date.now());
  }, [mounted, requireOrg, user, organization]);

  // Reset retry state when the signed-in user changes
  useEffect(() => {
    setOrgRetryAttempted(false);
    setOrgRetrying(false);
    setOrgRetryCount(0);
    setOrgRetryRequestId(0);
  }, [user?.id]);

  // Save view preference when in customer mode
  useEffect(() => {
    if (user && organization) {
      localStorage.setItem('currentViewMode', 'customer');
    }
  }, [user, organization]);

  const organizationRequiresMFA = organization?.settings
    ? (organization.settings as { mfa_required?: boolean } | null)?.mfa_required === true
    : false;
  const userRequiresMfaEnrollment = user ? isMfaRequired(user.email) : true;
  // Effective MFA requirement: org policy OR super_admin role (universal).
  // While the super-admin role lookup is unresolved we fail closed and
  // require MFA pessimistically — the force-enrolment screen gates on the
  // user not having a factor anyway, so a non-super-admin who lacks a
  // factor and is in a non-MFA org will still see the workspace once both
  // checks settle (their `setMfaRequired` resets to false on `!isSuperAdmin
  // && !organizationRequiresMFA`).
  const mfaRequirementSource: 'org' | 'super_admin' | 'super_admin_pending' | null =
    isSuperAdmin && userRequiresMfaEnrollment
      ? 'super_admin'
      : superAdminLookupPending && userRequiresMfaEnrollment
        ? 'super_admin_pending'
        : organizationRequiresMFA
          ? 'org'
          : null;
  const requiresMfaForUser = mfaRequirementSource !== null;

  // Check MFA requirement for this organization.
  // Only key off the stable org identity + MFA requirement so bootstrap snapshot churn
  // does not restart the check and flash the shell loader repeatedly.
  useEffect(() => {
    let isActive = true;
    const organizationIdAtStart = organization?.id ?? null;
    const nextMfaCheckKey = user && organization
      ? `${user.id}:${organization.id}:${requiresMfaForUser ? `mfa:${mfaRequirementSource}` : 'no-mfa'}`
      : null;

    const checkMFARequirement = async () => {
      if (!user || !organization) {
        if (!isActive) return;
        lastCompletedMfaCheckKeyRef.current = null;
        setMfaRequired(false);
        setHasMFA(false);
        setMfaCheckComplete(true);
        return;
      }

      if (lastCompletedMfaCheckKeyRef.current === nextMfaCheckKey) {
        if (!isActive) return;
        setMfaCheckComplete(true);
        return;
      }

      if (!isActive) return;
      setMfaCheckComplete(false);

      try {
        // This probe computes the org/super_admin MFA requirement + factor
        // state ONLY. It never writes `mfaSession` — the onAuthStateChange
        // listener above is the sole writer, so an in-flight probe cannot
        // clobber a session the listener refreshed mid-flight (ENT-3463 D1
        // TOCTOU, Codex PR #2783 round 2).
        if (!requiresMfaForUser) {
          setMfaRequired(false);
          setHasMFA(false);
          return;
        }

        // Fetch user factors when the org enforces MFA OR the user holds
        // super_admin role (ENT-1140: super admins must have MFA enrolled
        // regardless of org policy so the AAL2 gate on sensitive admin
        // actions is satisfiable).
        const { data: factorsData } = await supabase.auth.mfa.listFactors();
        if (!isActive || organization?.id !== organizationIdAtStart) {
          return;
        }

        const verifiedFactors = factorsData?.totp?.filter(f => f.status === 'verified') || [];
        setMfaRequired(true);
        setHasMFA(verifiedFactors.length > 0);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
        console.error('Error checking MFA requirement:', error);
        if (!isActive || organization?.id !== organizationIdAtStart) {
          return;
        }

        // Fail closed: if the factor probe errored, force enrolment.
        setMfaRequired(true);
        setHasMFA(false);
      } finally {
        if (isActive && organization?.id === organizationIdAtStart) {
          lastCompletedMfaCheckKeyRef.current = nextMfaCheckKey;
          setMfaCheckComplete(true);
        }
      }
    };

    checkMFARequirement();

    return () => {
      isActive = false;
    };
  }, [user?.id, user?.email, organization?.id, organizationRequiresMFA, isSuperAdmin, superAdminLookupPending, userRequiresMfaEnrollment, requiresMfaForUser, mfaRequirementSource]);

  const handleMFAEnabled = () => {
    setHasMFA(true);
    setMfaRequired(false);
  };

  // ENT-2770: once the enrollment gate is satisfied (MFA required AND a
  // verified factor exists), the *session* must also be aal2. An enrolled
  // user on an aal1 session (Back button off the /auth challenge, a deep
  // link, a restored tab) is forced through a step-up challenge instead of
  // reaching the workspace.
  //
  // Enable signal MUST be exactly `!isPreviewMfaBypass()` — the same carve-out
  // `startSignInMfaFlow` uses when it decides to challenge (auth flow
  // `requiresStepUp` is gated on `!previewMfaBypass` alone). Rationale:
  //   1. ENT-1418 local/preview bypass: `isPreviewMfaBypass()` is true there,
  //      so the gate stays inert and seeded aal1 sign-in reaches the workspace.
  //   2. NOT `isMfaRequired(email)` (Codex ENT-2770 P2): that also honours the
  //      per-email `VITE_DISABLE_MFA_ALLOWLIST`, but sign-in deliberately
  //      challenges allowlisted-yet-enrolled accounts anyway ("the allowlist
  //      must not skip a challenge for users who chose to enrol"). Gating on it
  //      would let an allowlisted enrolled user back-button into an aal1
  //      workspace.
  //   3. NOT `hasMFA`/`mfaRequired` (Codex ENT-2770 P1): those only reflect
  //      org-policy enrollment. The hook runs its OWN enrollment probe
  //      (`checkMFAStatus().enrolled`) and passes non-enrolled users through as
  //      `satisfied`, so a voluntary enrollee in a non-MFA org is still covered.
  //   4. D1 (ENT-3463): an SSO session delegates MFA to the customer IdP, so
  //      it is never challenged for an app TOTP step-up — even in the edge
  //      case where the same account also holds a verified factor (mirrors the
  //      controller's `!isIdpDelegatedMfaSession` guard on requiresStepUp).
  const stepUpGate = useAalStepUpGate(
    mfaCheckComplete && !isPreviewMfaBypass() && !isSsoSession(mfaSession),
  );

  // Short retry window: if we have a user but no org, refetch a few times before forcing onboarding.
  // This avoids transient org-context load issues (auth refresh / network / initial RLS timing).
  useEffect(() => {
    if (!mounted || isLoading) return;
    if (!requireOrg) return;
    if (!user) return;
    if (organization) return;

    let cancelled = false;
    const retryTimers: number[] = [];

    const wait = (delayMs: number) => new Promise<void>((resolve) => {
      if (delayMs === 0) {
        resolve();
        return;
      }
      const timer = window.setTimeout(resolve, delayMs);
      retryTimers.push(timer);
    });

    const refetchWithTimeout = async () => {
      let timeoutId: number | null = null;
      try {
        await Promise.race([
          Promise.resolve(refetch()),
          new Promise<void>((_, reject) => {
            timeoutId = window.setTimeout(() => reject(new Error('org_refetch_timeout')), ORG_RECOVERY_REFETCH_TIMEOUT_MS);
          }),
        ]);
      } catch {
        // swallow: we'll fall back to onboarding after retries
      } finally {
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }
      }
    };

    setOrgRetryAttempted(true);
    setOrgRetrying(true);
    setOrgRetryCount(0);

    const runRetryLoop = async () => {
      const delays = [0, 400, 900];
      for (let attemptIndex = 0; attemptIndex < delays.length; attemptIndex += 1) {
        await wait(delays[attemptIndex]);
        if (cancelled) return;
        await refetchWithTimeout();
        if (cancelled) return;
        setOrgRetryCount(attemptIndex + 1);
      }
      if (!cancelled) {
        setOrgRetrying(false);
      }
    };

    void runRetryLoop();

    return () => {
      cancelled = true;
      retryTimers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [mounted, isLoading, requireOrg, user?.id, organization?.id, refetch, orgRetryRequestId]);

  const handleReturnToMsp = async () => {
    if (isReturningToMspRef.current) {
      return;
    }

    const exitRoute = activeExitRouteRef.current;
    setIsReturningToMsp(true);
    try {
      await returnToMspOrg();
    } catch (error) {
      console.error('Failed to restore MSP org before exiting customer view', error);
    } finally {
      if (isMountedRef.current && activeExitRouteRef.current === exitRoute) {
        navigate('/customers', { replace: true });
      }
      if (isMountedRef.current) {
        setIsReturningToMsp(false);
      }
    }
  };

  const handleRetryOrganizationResolution = () => {
    setSpinnerSince(Date.now());
    setOrgRetryAttempted(false);
    setOrgRetrying(false);
    setOrgRetryCount(0);
    setOrgRetryRequestId((requestId) => requestId + 1);
  };

  const handleReturnToSignIn = async () => {
    const { signOutLocally } = await import('@/lib/auth');
    await signOutLocally('/auth');
  };

  const isCustomerViewHeader = isMspContext && mspHomeOrg;
  const hasHeaderAccessory = headerAccessory !== null;
  const shouldStackCustomerHeaderAccessory = isCustomerViewHeader && hasHeaderAccessory;
  const shellHeaderAccessory = isValidElement(headerAccessory) && typeof headerAccessory.type !== 'string'
    ? cloneElement(headerAccessory as ReactElement<{ topbarSize?: 'compact' | 'toolbar' }>, {
        topbarSize: shouldStackCustomerHeaderAccessory ? 'toolbar' : 'compact',
      })
    : headerAccessory;
  const {
    visibleNotices: visibleSupportNotices,
    isLoading: isSupportNoticeLoading,
    errorMessage: supportNoticeErrorMessage,
  } = useActiveSupportNoticeBannerState();
  const shouldHideSupportNotices = portalSwitchIntent !== null;
  const isAuthBootstrapLoading = !mounted || isLoading;

  useEffect(() => {
    if (portalSwitchIntent?.target !== 'workspace') {
      return;
    }

    if (isSupportNoticeLoading) {
      return;
    }

    clearPortalSwitchIntent('workspace');
  }, [portalSwitchIntent?.target, isSupportNoticeLoading, organization?.id, supportNoticeErrorMessage]);

  if (!user) {
    if (isAuthBootstrapLoading) {
      return <FullPageAppLoadingState message="Loading your workspace…" />;
    }

    // ENT-1958: preserve the deep-link destination through sign-in + MFA
    // by routing the signed-out caller to /auth with an encoded returnTo,
    // which Auth.tsx restores after authentication.
    return <Navigate to={buildAuthRedirectPath(location)} replace />;
  }

  // SECURITY: Hard enforcement - if in recovery mode, block access to protected routes
  // User must complete password reset before accessing any app functionality
  // Note: We don't redirect here because it causes a loop - Index.tsx handles showing the reset form
  if (shouldBlockProtectedRouteDuringRecovery(location.pathname)) {
    console.log('[Security] Recovery mode active in AppLayout - blocking dashboard access');
    // Return a loading state instead of redirecting to prevent loops
    // The user should be on Index.tsx which will show the password reset form
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4 p-8">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-semibold mb-2">Password Reset Required</h2>
          <p className="text-muted-foreground mb-4">
            You must complete the password reset process before accessing the platform.
          </p>
          {/* ENT-4311: point at the resumable /auth/reset route — it reconstructs the
              in-progress recovery (update-password/MFA) from the live session, whereas `/`
              only stops routing and would strand the reset. /auth/reset is a recovery path
              (isRecoveryPath), so it is not blocked while recovery mode is active. */}
          <a href="/auth/reset" className="text-primary hover:underline">
            Return to password reset
          </a>
        </div>
      </div>
    );
  }

  const shouldRetryOrganizationResolution =
    requireOrg && !organization && (!orgRetryAttempted || orgRetrying || orgRetryCount < 3);

  if (
    requireOrg
    && !organization
    && bootstrapError
    && !orgRetrying
    && !shouldRetryOrganizationResolution
  ) {
    return (
      <OrganizationBootstrapErrorState
        message={
          bootstrapError === 'timeout'
            ? 'Sign-in completed, but loading your organisation timed out. Retry to continue, or return to sign in and try again.'
            : 'Sign-in completed, but we could not load your organisation. Retry to continue, or return to sign in if the issue persists.'
        }
        onRetry={handleRetryOrganizationResolution}
        onSignOut={handleReturnToSignIn}
      />
    );
  }

  if (requireOrg && !organization && !shouldRetryOrganizationResolution) {
    return <Navigate to="/onboarding" replace />;
  }

  const authenticatedContent = shouldRetryOrganizationResolution ? (
    <FullPageAppLoadingState
      message={
        spinnerSince && Date.now() - spinnerSince > 2500
        ? `Loading your organisation… (attempt ${Math.min(orgRetryCount + 1, 3)} of 3)`
        : 'Loading your organisation…'
      }
    />
  ) : !mfaCheckComplete ? (
    <FullPageAppLoadingState message="Loading your workspace…" />
  ) : isOrgMfaEnrollmentRequired({
      session: mfaSession,
      orgMfaRequired: mfaRequired,
      userHasVerifiedFactor: hasMFA,
      // previewBypass is intentionally false here: the enrolment screen has
      // never honoured the ENT-1418 preview bypass (only the step-up gate and
      // the sign-in controller do). Passing false preserves that behaviour and
      // adds ONLY the D1 SSO carve-out (ENT-3463).
      previewBypass: false,
      mfaRequirementSource,
    }) && organization ? (
    <OrgMFAEnforcementScreen
      organizationName={organization.name}
      onMFAEnabled={handleMFAEnabled}
    />
  ) : stepUpGate.status === 'step-up-required' && stepUpGate.factorId ? (
    // ENT-2770: enrolled but the session is aal1 — challenge before workspace.
    <MFAVerification
      factorId={stepUpGate.factorId}
      onSuccess={stepUpGate.recheck}
      onCancel={handleReturnToSignIn}
    />
  ) : stepUpGate.status === 'checking' ? (
    <FullPageAppLoadingState message="Verifying your two-factor session…" />
  ) : stepUpGate.status === 'unavailable' ? (
    // Fail closed: the assurance probe could not confirm an aal2 session, so
    // the workspace stays withheld. Offer a retry / sign-out rather than a
    // dead loader.
    <OrganizationBootstrapErrorState
      message="We couldn't verify your two-factor session. Retry, or return to sign in and try again."
      onRetry={stepUpGate.recheck}
      onSignOut={handleReturnToSignIn}
    />
  ) : (
    <SidebarProvider condensedInsetPx={SIDEBAR_WIDTH_ICON_PX}>
      <div className="relative flex min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
            <AppSidebar />
            <SidebarInset className="flex-1 min-w-0 h-svh overflow-hidden bg-transparent">
            <GeometricFacetBackground
              tone="workspace"
              idPrefix="app-shell-depth"
              testId="app-shell-depth-layer"
              className="pointer-events-none absolute inset-0 z-0 overflow-hidden [mask-image:linear-gradient(to_bottom,rgba(0,0,0,0.98)_0%,rgba(0,0,0,0.88)_34%,rgba(0,0,0,0.42)_58%,rgba(0,0,0,0.1)_74%,transparent_92%)]"
              glowClassName="absolute left-1/2 top-[-6%] h-[24rem] w-[40rem] -translate-x-1/2 rounded-full bg-[radial-gradient(circle_at_top,_rgba(99,102,241,0.18),_rgba(255,255,255,0)_62%)] opacity-95 blur-3xl"
              washClassName="absolute inset-0 bg-[radial-gradient(circle_at_50%_14%,_rgba(196,181,253,0.26),_rgba(255,255,255,0)_24%),radial-gradient(circle_at_50%_68%,_rgba(167,139,250,0.18),_rgba(255,255,255,0)_26%)] opacity-90"
              svgClassName="absolute top-[-16%] left-1/2 h-[120%] w-[118%] -translate-x-1/2 opacity-[0.26]"
            />
            {isWorkspaceNoticeRoute ? (
              <SupportNoticeBanner
                notices={shouldHideSupportNotices ? [] : visibleSupportNotices}
                isLoading={isSupportNoticeLoading || shouldHideSupportNotices}
                errorMessage={supportNoticeErrorMessage}
              />
            ) : null}
            <header
              data-testid="app-shell-header"
              className={[
                'sticky top-0 z-30 shrink-0 border-b px-4 sm:px-6 backdrop-blur-sm',
                hasHeaderAccessory
                  ? shouldStackCustomerHeaderAccessory
                    ? 'flex flex-col justify-center gap-0 pt-3 pb-1 min-[1280px]:py-2'
                    : 'flex min-h-14 flex-col gap-3 py-3 min-[1180px]:h-14 min-[1180px]:py-0'
                  : 'flex h-14 items-center justify-between gap-4',
                isCustomerViewHeader
                  ? 'border-purple-700/40 bg-gradient-to-r from-purple-700 to-violet-700 text-white shadow-md'
                  : 'border-border/30 bg-white/50 dark:bg-slate-900/50',
              ].join(' ')}
            >
              <div
                data-testid="app-shell-header-content"
                className={[
                  'w-full min-w-0 items-center gap-x-3 gap-y-3',
                  hasHeaderAccessory
                    ? shouldStackCustomerHeaderAccessory
                      ? 'grid grid-cols-[minmax(0,1fr)_auto] min-[1280px]:grid-cols-[minmax(17rem,1fr)_minmax(34rem,44rem)_minmax(17rem,1fr)] min-[1280px]:gap-y-0'
                      : 'grid grid-cols-[minmax(0,1fr)_auto] min-[1180px]:h-full min-[1180px]:grid-cols-[minmax(0,1fr)_minmax(32rem,45rem)_minmax(0,1fr)]'
                    : 'flex justify-between',
                ].join(' ')}
              >
                <div className="flex min-w-0 items-center gap-3">
                  {!shouldStackCustomerHeaderAccessory ? (
                    <SurfaceSidebarContextToggleButton
                      className={[
                        '-ml-2',
                        isCustomerViewHeader ? 'text-white hover:bg-white/15 hover:text-white' : '',
                      ].join(' ')}
                    />
                  ) : null}
                  {isCustomerViewHeader ? (
                    <div className="flex min-w-0 items-center gap-3">
                      {shouldStackCustomerHeaderAccessory ? (
                        <>
                          <SurfaceSidebarContextToggleButton className="text-white hover:bg-white/15 hover:text-white" />
                          <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
                            <div className="min-w-0 w-full">
                              <FitToWidthText
                                className="font-semibold leading-tight text-white"
                                maxFontPx={18}
                                minFontPx={12}
                                text={<><span className="font-normal">Viewing as</span> {organization?.name}</>}
                              />
                              <FitToWidthText
                                className="text-purple-100 leading-tight"
                                maxFontPx={14}
                                minFontPx={10}
                                text={`Managed by ${mspHomeOrg.name}`}
                              />
                            </div>
                            <div className="hidden sm:flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-white/95">
                              <Eye className="h-4 w-4" />
                              <span>{CUSTOMER_PROFILE_LABEL}</span>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="hidden sm:flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-white/95">
                          <Eye className="h-4 w-4" />
                          <span>{CUSTOMER_PROFILE_LABEL}</span>
                        </div>
                      )}
                      {!shouldStackCustomerHeaderAccessory ? (
                        <div className="min-w-0 w-full">
                          <FitToWidthText
                            className="font-semibold text-white"
                            maxFontPx={14}
                            minFontPx={10}
                            text={<><span className="font-normal">Viewing as</span> {organization?.name}</>}
                          />
                          <FitToWidthText
                            className="text-purple-100"
                            maxFontPx={12}
                            minFontPx={9}
                            text={`Managed by ${mspHomeOrg.name}`}
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                {hasHeaderAccessory ? (
                  <div
                    data-testid="app-shell-header-accessory"
                    className={[
                      'col-span-full min-w-0 items-center order-3',
                      shouldStackCustomerHeaderAccessory
                        ? 'grid w-full grid-cols-[minmax(0,1fr)_minmax(0,44rem)_minmax(0,1fr)] items-center gap-x-4 min-[1280px]:order-none min-[1280px]:col-span-1 min-[1280px]:col-start-2 min-[1280px]:row-start-1 min-[1280px]:grid-cols-1 min-[1280px]:justify-self-center min-[1280px]:self-center'
                        : 'flex w-full justify-center min-[1180px]:order-none min-[1180px]:col-span-1 min-[1180px]:col-start-2 min-[1180px]:row-start-1 min-[1180px]:self-center',
                    ].join(' ')}
                  >
                    {shouldStackCustomerHeaderAccessory ? (
                      <div aria-hidden="true" className="min-[1280px]:hidden" />
                    ) : null}
                    <div className={shouldStackCustomerHeaderAccessory ? 'min-w-0 justify-self-center w-full max-w-[44rem]' : 'min-w-0 w-full max-w-[45rem]'}>
                      {shellHeaderAccessory}
                    </div>
                    {shouldStackCustomerHeaderAccessory ? <div aria-hidden="true" className="min-[1280px]:hidden" /> : null}
                  </div>
                ) : (
                  <div className="hidden min-[1600px]:block min-w-0 flex-1" />
                )}

                <div
                  data-testid="app-shell-header-actions"
                  className={[
                    'flex shrink-0 items-center justify-end gap-2',
                    shouldStackCustomerHeaderAccessory ? 'col-start-2 row-start-1 min-[1280px]:col-start-3 min-[1280px]:row-start-1' : hasHeaderAccessory ? 'min-[1180px]:col-start-3 min-[1180px]:row-start-1' : '',
                  ].join(' ')}
                >
                  {isCustomerViewHeader && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleReturnToMsp}
                      disabled={isReturningToMsp}
                      className="gap-2 text-white hover:bg-white/15 hover:text-white"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      <span className="hidden sm:inline">Exit {CUSTOMER_PROFILE_LABEL}</span>
                      <span className="sm:hidden">Exit</span>
                      <span className="sr-only sm:hidden"> {CUSTOMER_PROFILE_LABEL}</span>
                    </Button>
                  )}
                  <div
                    data-testid="app-shell-notification-action"
                    className={[
                      'flex min-w-0 items-center gap-2',
                      isCustomerViewHeader
                        ? '[&_button]:text-white [&_button:hover]:bg-white/15 [&_button:hover]:text-white'
                        : '',
                    ].join(' ')}
                  >
                    <SuperAdminDebugOverlay />
                    <NotificationBell />
                  </div>
                </div>
              </div>
            </header>
            <BreakGlassCountdownBanner />
            <div className="platform-view-pane flex-1 min-w-0 overflow-hidden">
              <main
                className="relative z-10 h-full overflow-y-auto overflow-x-hidden p-6"
              >
                {isReturningToMsp ? (
                  <AppShellRouteLoadingState message="Returning to MSP workspace…" />
                ) : (
                  <SubscriptionGate loadingFallback={<AppShellRouteLoadingState message="Loading your subscription…" />}>
                    <AppShellHeaderAccessoryContext.Provider value={{ isAvailable: true, setHeaderAccessory }}>
                      <Suspense fallback={<AppShellRouteLoadingState message="Loading page…" />}>
                        {children || <Outlet />}
                      </Suspense>
                    </AppShellHeaderAccessoryContext.Provider>
                  </SubscriptionGate>
                )}
              </main>
            </div>
            
          {/* SupplySteward FAB */}
            <AgentFAB organizationId={organization?.id} />
            <DebugClipboardButton />
            <SuperAdminHintsControl />
          </SidebarInset>
        </div>
    </SidebarProvider>
  );

  return (
    <SessionManager timeoutMinutes={sessionTimeoutMinutes} warningMinutes={2}>
      {authenticatedContent}
    </SessionManager>
  );
}
